import React, { useState } from "react";
import axios from "axios";
import { Mail, Phone, MessageCircle, Globe } from "lucide-react";
import { BRAND, wa } from "@/lib/brand";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useTitle } from "@/lib/useTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { name: "", company: "", country: "", email: "", phone: "", message: "", website: "" };

const Contact = () => {
  useTitle("Contact | GN Agro Traders — Indian Agro Commodity Exporter",
    "Contact GN Agro Traders for bulk rice, pulses, wheat and agro commodity export enquiries. Phone, WhatsApp and email.");
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const on = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.company || !f.country || !f.email || !f.phone) { toast.error("Please fill all required fields"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/enquiries`, { ...f, incoterm: "", packaging: "", destination_port: "", delivery_timeline: "", items: [], source: "contact-page", page: window.location.pathname });
      toast.success("Message sent");
      nav(`/enquiry-confirmed/${data.id}`);
    } catch (err) {
      toast.error("Could not submit. Please try again or WhatsApp us.");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <section className="bg-[#004225] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-[11px] font-eyebrow text-[#D4AF37]"><span className="gold-line mr-3" />Contact</div>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl">Talk to GN Agro Traders</h1>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-2 gap-10">
        <div>
          <div className="text-[11px] font-eyebrow text-[#B5942D]">Reach Us</div>
          <h2 className="mt-2 font-display text-2xl text-[#004225]">{BRAND.name}</h2>
          <p className="text-[13px] text-[#171717]/70">{BRAND.legal}</p>
          <div className="mt-5 space-y-3 text-[14px]">
            <a href={`tel:${BRAND.phoneTel}`} className="flex items-center gap-2 text-[#171717] hover:text-[#004225]"><Phone className="h-4 w-4 text-[#D4AF37]" />{BRAND.phone} (Primary)</a>
            <a href={`tel:${BRAND.phone2Tel}`} className="flex items-center gap-2 text-[#171717] hover:text-[#004225]"><Phone className="h-4 w-4 text-[#D4AF37]" />{BRAND.phone2} (Secondary)</a>
            <a href={`mailto:${BRAND.email}`} className="flex items-center gap-2 text-[#171717] hover:text-[#004225]"><Mail className="h-4 w-4 text-[#D4AF37]" />{BRAND.email}</a>
            <a href={BRAND.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#171717] hover:text-[#004225]"><Globe className="h-4 w-4 text-[#D4AF37]" />gnagrotraders.com</a>
            <a href={wa()} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-3 rounded-full bg-[#25D366] text-white px-4 py-2 text-[11px] font-semibold uppercase tracking-widest"><MessageCircle className="h-4 w-4" />WhatsApp us</a>
          </div>
        </div>
        <form onSubmit={submit} className="bg-white border border-[#004225]/10 rounded-sm p-6 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="contact-form">
          <div><Label>Name *</Label><Input value={f.name} onChange={on("name")} data-testid="contact-name" required /></div>
          <div><Label>Company *</Label><Input value={f.company} onChange={on("company")} data-testid="contact-company" required /></div>
          <div><Label>Country *</Label><Input value={f.country} onChange={on("country")} required /></div>
          <div><Label>Email *</Label><Input type="email" value={f.email} onChange={on("email")} required /></div>
          <div className="sm:col-span-2"><Label>Phone / WhatsApp *</Label><Input value={f.phone} onChange={on("phone")} required /></div>
          <div className="sm:col-span-2"><Label>Message</Label><Textarea rows={4} value={f.message} onChange={on("message")} placeholder="Product, quantity, destination, or general enquiry…" /></div>
          <input type="text" name="website" value={f.website} onChange={on("website")} tabIndex={-1} autoComplete="off" style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0 }} aria-hidden="true" />
          <div className="sm:col-span-2"><Button type="submit" disabled={busy} data-testid="contact-submit" className="btn-primary rounded-full px-6 uppercase tracking-widest text-[11px]">{busy ? "Sending…" : "Send Message"}</Button></div>
        </form>
      </section>
    </div>
  );
};

export default Contact;
